import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { jwtDecode } from "jwt-decode";
import { LoginResponseType, UserLoginType } from "../types/login";
import CapitalizationUtil from "../utils/CapitalizationUtil";
import { COMMON_API_URL } from "../constants/urls";

type AuthContextType = {
  jwt: string | null;
  userID: string | null;
  headOff: string | null;
  userName: string | null;
  setAuthInfo: (jwt: string) => void;
  logOut: () => void;
  logIn: (username: string, password: string) => Promise<void>;
  isAdmin: boolean;
  sessionStart: number;
  sessionEnd: number;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [jwt, setJwt] = useState<string | null>(null);
  const [userID, setUserID] = useState<string | null>(null);
  const [headOff, setHeadOff] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [sessionStart, setSessionStart] = useState<number | null>(null);
  const [sessionEnd, setSessionEnd] = useState<number | null>(null);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      const { jwt, userID, headOff, userName } = JSON.parse(storedUser);
      setJwt(jwt);
      setUserID(userID);
      setHeadOff(headOff);
      setUserName(userName);
    }
  }, []);

  const setAuthInfo = (token: string) => {
    const decodedToken: UserLoginType =
      CapitalizationUtil.convertKeysToCamelCase(jwtDecode(token));

    const userInfo = {
      jwt: token,
      userID: decodedToken.userID,
      userName: decodedToken.userName,
      headOff: decodedToken.headOff,
    };

    setJwt(token);
    setUserID(decodedToken.userID);
    setHeadOff(decodedToken.headOff);
    setUserName(decodedToken.userName);
    setSessionStart(decodedToken.iat);
    setSessionEnd(decodedToken.exp);

    sessionStorage.setItem("user", JSON.stringify(userInfo));
  };

  const logOut = () => {
    setJwt(null);
    setUserID(null);
    setHeadOff(null);
    sessionStorage.removeItem("user");
  };

  const logIn = async (username: string, password: string): Promise<void> => {
    try {
      const response = await fetch(`${COMMON_API_URL}/login`, {
        method: "POST",
        body: JSON.stringify({
          login: username,
          password,
        }),
      });

      if (!response.ok) {
        const error = CapitalizationUtil.convertKeysToCamelCase(
          await response.json()
        );
        throw new Error(error.message || "Грешка при влизане");
      }

      const result: LoginResponseType =
        CapitalizationUtil.convertKeysToCamelCase(await response.json());
      const token = result.token;
      setAuthInfo(token);
    } catch (err: any) {
      throw new Error(err.message || "Грешка при влизане");
    }
  };

  const isAdmin = userID && Number(userID) < 100;

  const authValue = useMemo(
    () => ({
      jwt,
      userID,
      headOff,
      userName,
      setAuthInfo,
      logOut,
      logIn,
      isAdmin,
      sessionEnd,
      sessionStart,
    }),
    [jwt, userID, headOff, userName]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};
